import React, { useRef } from "react";
import lottie from "lottie-web";
import { defineElement } from "lord-icon-element";

// register lottie and define custom element
defineElement(lottie.loadAnimation);

export type LordIconTrigger =
    | "hover"
    | "click"
    | "loop"
    | "loop-on-hover"
    | "morph"
    | "morph-two-way";

export type LordIconColors = {
    primary?: string;
    secondary?: string;
};

export type LordIconProps = {
    src?: string;
    trigger?: LordIconTrigger;
    colors?: LordIconColors;
    delay?: number;
    size?: string;

    className?: string;

    onPlay?(f: () => void): void;
    onPause?(f: () => void): void;
    onStop?(f: () => void): void;
};

export const LordIcon = ({ colors, src, size, delay, className, onPlay, onPause, onStop }: LordIconProps) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const iconRef = useRef<any>(null);

    const play = () => {
        if (iconRef.current?.player?.play) {
            iconRef.current.setAttribute("trigger", "loop");
            iconRef.current.player.play();
        }
    };

    const pause = () => {
        if (iconRef.current?.player?.pause) {
            iconRef.current.player.pause();
        }
    };

    const stop = () => {
        if (iconRef.current?.player?.stop) {
            iconRef.current.player.stop();
        }
    };
      
    // Register the methods with the passed in props
    React.useEffect(() => {
      if (onPlay) {
        onPlay(play);
      }
      if (onPause) {
        onPause(pause);
      }
      if (onStop) {
        onStop(stop);
      }
    }, [onPlay, onPause, onStop]);

    return (
        <lord-icon
            ref={iconRef}
            class={className}
            colors={`primary:${colors?.primary},secondary:${colors?.secondary}`}
            src={src}
            // trigger={trigger} // in start() above
            delay={delay}
            style={{
                width: size,
                height: size,
            }}
        />
    );
};

export const LordIconStatic = ({ size, className }: LordIconProps) => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 500 500" className={className}>
            <defs>
                <clipPath id="c">
                    <path d="M0 0h500v500H0z" />
                </clipPath>
                <clipPath id="b">
                    <path d="M0 0h500v500H0z" />
                </clipPath>
                <filter id="e" width="300%" height="300%" x="-100%" y="-100%">
                    <feGaussianBlur result="filter_result_0" stdDeviation="52.5 52.5" />
                </filter>
                <mask id="d" mask-type="alpha">
                    <use href="#a" />
                </mask>
                <g
                    id="a"
                    fill-opacity="0"
                    stroke="#121331"
                    stroke-width="14"
                    clip-path="url(#b)"
                    style={{ display: "block" }}>
                    <g style={{ display: "block" }}>
                        <path d="M308 149v128c0 33.136-26.864 60-60 60-33.136 0-60-26.864-60-60V149c0-33.136 26.864-60 60-60 33.136 0 60 26.864 60 60zm-60 129.82.124 56.124m-59.376-56.124 119.252.056" />
                        <path
                            stroke-linecap="round"
                            d="M306.252 250.556h-21m21-24h-12m-105.564 23.992h21.248m-21.248-24h12.144"
                        />
                    </g>
                    <g stroke-linecap="round" style={{ display: "block" }}>
                        <path
                            stroke-linejoin="round"
                            d="M336 256.752V279c0 48.6-39.4 88-88 88s-88-39.4-88-88v-22.5"
                        />
                        <path d="M213.124 409h73m-36.376-41.5V409" />
                    </g>
                    <path
                        stroke-linecap="round"
                        stroke-opacity="0"
                        d="M160 215v-1.5m-30.124 1.5v-1.5m-30 1.5v-1.5m-30 1.5v-1.5m-30 1.5v-1.5m120.124 1v1.5m-30.124-1.5v1.5m-30-1.5v1.5m-30-1.5v1.5m-30-1.5v1.5M336 215.5V214m30.124 1.5V214m30 1.5V214m30 1.5V214m30 1.5V214M336 216v1.5m30.124-1.5v1.5m30-1.5v1.5m30-1.5v1.5m30-1.5v1.5"
                        style={{ display: "block" }}
                    />
                </g>
            </defs>
            <g clip-path="url(#c)">
                <g mask="url(#d)" style={{ display: "block" }}>
                    <g
                        fill="#4286D7"
                        filter="url(#e)"
                        transform="rotate(-94 262.075 8.435) scale(2.4)">
                        <path
                            d="M0-250c137.975 0 250 112.025 250 250S137.975 250 0 250-250 137.975-250 0-137.975-250 0-250z"
                            className="design"
                        />
                        <path
                            d="M113.242-295.384c97.478 0 176.5 79.022 176.5 176.5s-79.022 176.5-176.5 176.5c-43.948 0-74.396-34.057-105.29-60.631-37.631-32.369-71.21-62.338-71.21-115.869 0-97.478 79.022-176.5 176.5-176.5z"
                            className="design"
                        />
                    </g>
                </g>
                <path style={{ display: "none" }} />
            </g>
        </svg>
    );
};
